import { LoadingState } from '@nolens/ui';
import { lazy, Suspense, type ComponentType } from 'react';
import { createBrowserRouter, Navigate, Outlet } from 'react-router';
import { UpdatePrompt } from '../lib/pwa';
import { DevLoginPage } from '../routes/DevLoginPage';
import { HomeRedirect } from '../routes/HomeRedirect';
import { MfaPage } from '../routes/MfaPage';
import { InvitePage } from '../routes/InvitePage';
import { LoginPage } from '../routes/LoginPage';
import { RequireSession } from '../routes/RequireSession';
import { NotFound, RouteError } from '../routes/RouteError';

// Code splitting por superfície: o utente nunca descarrega o código da clínica (ADR-022).
const clinic = () => import('../surfaces/clinic');
const patient = () => import('../surfaces/patient');

function fromModule<M>(load: () => Promise<M>, pick: (m: M) => ComponentType) {
  const Lazy = lazy(() => load().then((m) => ({ default: pick(m) })));
  return function LazyRoute() {
    return (
      <Suspense fallback={<LoadingState />}>
        <Lazy />
      </Suspense>
    );
  };
}

const C = {
  Layout: fromModule(clinic, (m) => m.ClinicLayout),
  Index: fromModule(clinic, (m) => m.ClinicIndex),
  Attention: fromModule(clinic, (m) => m.AttentionPage),
  Patients: fromModule(clinic, (m) => m.PatientsPage),
  Patient: fromModule(clinic, (m) => m.PatientPage),
  PatientSummary: fromModule(clinic, (m) => m.PatientSummaryPage),
  Plan: fromModule(clinic, (m) => m.PlanBuilderPage),
  Library: fromModule(clinic, (m) => m.LibraryPage),
  Messages: fromModule(clinic, (m) => m.ClinicMessagesPage),
  Settings: fromModule(clinic, (m) => m.SettingsLayout),
  Branding: fromModule(clinic, (m) => m.BrandingSettingsPage),
  ClinicSettings: fromModule(clinic, (m) => m.ClinicSettingsPage),
  Team: fromModule(clinic, (m) => m.TeamSettingsPage),
  CheckInSettings: fromModule(clinic, (m) => m.CheckInSettingsPage),
  Audit: fromModule(clinic, (m) => m.AuditPage),
  Results: fromModule(clinic, (m) => m.ResultsPage),
};

const P = {
  Layout: fromModule(patient, (m) => m.PatientLayout),
  Tabs: fromModule(patient, (m) => m.PatientTabs),
  Player: fromModule(patient, (m) => m.PlayerPage),
  CheckIn: fromModule(patient, (m) => m.CheckInPage),
  Today: fromModule(patient, (m) => m.TodayPage),
  Week: fromModule(patient, (m) => m.WeekPage),
  Messages: fromModule(patient, (m) => m.PatientMessagesPage),
  Profile: fromModule(patient, (m) => m.ProfilePage),
};

function Root() {
  return (
    <>
      <Outlet />
      <UpdatePrompt />
    </>
  );
}

export const router = createBrowserRouter([
  {
  element: <Root />,
  children: [
  { path: '/entrar', element: <LoginPage />, errorElement: <RouteError /> },
  { path: '/convite/:token', element: <InvitePage />, errorElement: <RouteError /> },
  // Login de desenvolvimento (ADR-032): a rota nem existe no build de produção.
  ...(import.meta.env.DEV ? [{ path: '/dev/entrar', element: <DevLoginPage /> }] : []),
  {
    element: <RequireSession />,
    errorElement: <RouteError />,
    children: [
      { index: true, element: <HomeRedirect /> },
      { path: 'verificacao', element: <MfaPage /> },
      {
        path: 'c/:orgId',
        element: <C.Layout />,
        children: [
          { index: true, element: <C.Index /> },
          { path: 'atencao', element: <C.Attention /> },
          { path: 'utentes', element: <C.Patients /> },
          { path: 'utentes/:patientId', element: <C.Patient /> },
          { path: 'utentes/:patientId/resumo', element: <C.PatientSummary /> },
          { path: 'planos/:planId', element: <C.Plan /> },
          { path: 'biblioteca', element: <C.Library /> },
          { path: 'mensagens', element: <C.Messages /> },
          { path: 'resultados', element: <C.Results /> },
          {
            path: 'definicoes',
            element: <C.Settings />,
            children: [
              { index: true, element: <Navigate to="equipa" replace /> },
              { path: 'marca', element: <C.Branding /> },
              { path: 'clinica', element: <C.ClinicSettings /> },
              { path: 'equipa', element: <C.Team /> },
              { path: 'check-in', element: <C.CheckInSettings /> },
              { path: 'atividade', element: <C.Audit /> },
            ],
          },
        ],
      },
      {
        path: 'p/:orgId',
        element: <P.Layout />,
        children: [
          { index: true, element: <Navigate to="hoje" replace /> },
          {
            element: <P.Tabs />,
            children: [
              { path: 'hoje', element: <P.Today /> },
              { path: 'semana', element: <P.Week /> },
              { path: 'mensagens', element: <P.Messages /> },
              { path: 'perfil', element: <P.Profile /> },
            ],
          },
          { path: 'sessao', element: <P.Player /> },
          { path: 'check-in', element: <P.CheckIn /> },
        ],
      },
    ],
  },
  { path: '*', element: <NotFound /> },
],
  },
]);
